import { Box, Section } from '@factorialco/deprecated-design-system'

import NotFound from 'modules/core/components/NotFound'
import i18n from 'modules/core/lib/i18n'

import Page from 'modules/layout/components/Page'
import Header from 'modules/layout/components/Page/Header'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { useQuestionGroup } from 'modules/reviews/hooks/useQuestionGroup'
import { useQuestionGroupCompetencies } from 'modules/reviews/hooks/useQuestionGroupsCompetencies'
import type { SplitView as SplitViewType } from 'modules/reviews/lib/useReviewsSplitData'
import { useReviewsSplitViewData } from 'modules/reviews/lib/useReviewsSplitData'
import { useQuestionGroups } from 'modules/reviews/pages/performance/ReviewsReviewee/useQuestionGroups'
import reviews from 'modules/reviews/store/collections/v2/reviews'

import Body from './Body'

interface Props {
  formId: number
  userId: number
  back: string
  canSeeCompetencies: boolean
  useResponsivePage: boolean
}

export interface EnsureSplitView {
  managerQuestionGroupId: number
  selfQuestionGroupId: number
  canSeeSplitView: boolean
}

const ensureSplitViewData = (splitViewData: SplitViewType): EnsureSplitView => {
  if (!splitViewData.selfQuestionGroupId || !splitViewData.managerQuestionGroupId)
    throw new Error('You do not have groups matching the condition for this page')

  return {
    managerQuestionGroupId: splitViewData.managerQuestionGroupId,
    selfQuestionGroupId: splitViewData.selfQuestionGroupId,
    canSeeSplitView: splitViewData.canSeeSplitView,
  }
}

const SplitView = ({ formId, userId, back, canSeeCompetencies, useResponsivePage }: Props) => {
  const reviewModel = reviews.get(+formId)
  const { loading: loadingCompetencies } = useQuestionGroupCompetencies(canSeeCompetencies)
  const { loading: loadingQuestionGroups, questionGroups } = useQuestionGroups(formId, userId)
  const splitViewData = useReviewsSplitViewData(questionGroups)
  const ensuredSplitViewData = ensureSplitViewData(splitViewData)
  const { loading: loadingManagerQuestionGroup, questionGroup: managerQuestionGroup } =
    useQuestionGroup(ensuredSplitViewData.managerQuestionGroupId, true)
  const { loading: loadingSelfQuestionGroup, questionGroup: selfQuestionGroup } = useQuestionGroup(
    ensuredSplitViewData.selfQuestionGroupId,
    true
  )

  const isLoading =
    loadingQuestionGroups ||
    loadingManagerQuestionGroup ||
    loadingSelfQuestionGroup ||
    loadingCompetencies

  if (useResponsivePage) {
    return (
      <ResponsivePage.Body loading={isLoading} error={!reviewModel}>
        {managerQuestionGroup && selfQuestionGroup && reviewModel && (
          <Box>
            <Body
              managerQuestionGroup={managerQuestionGroup}
              selfQuestionGroup={selfQuestionGroup}
              assessCompetencies={reviewModel.assessCompetencies}
            />
          </Box>
        )}
      </ResponsivePage.Body>
    )
  }

  if (!reviewModel) return <NotFound />

  return (
    <Page isLoading={isLoading}>
      <Page.Header>
        <Header.Title backTo={back}>{reviewModel.name}</Header.Title>
      </Page.Header>
      <Page.Body>
        {managerQuestionGroup && selfQuestionGroup && (
          <Section.FullWidth
            title={i18n.t('surveys.review.reviews.split_view.title')}
            illustration="user-interaction-review_chart"
            description={i18n.t('surveys.review.reviews.split_view.description')}
          >
            <Body
              managerQuestionGroup={managerQuestionGroup}
              selfQuestionGroup={selfQuestionGroup}
              assessCompetencies={reviewModel.assessCompetencies}
            />
          </Section.FullWidth>
        )}
      </Page.Body>
    </Page>
  )
}

export default SplitView
